import { useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, DataTable, Text } from "grommet";
import { DataContext } from "grommet/contexts/DataContext";
import {
  claimedIdsSignature,
  formatCreatedAtCentral,
  queueRowClassName,
  queuePodOutlineRowClassName,
  queueRowProps,
} from "../PodV2/podQueueList";
import {
  formatWaitingDuration,
  subscribeWaitingClock,
} from "./waitingClock";
import { isNotTawkSession, sessionTawkLabel } from "./tawkSession";

export function openPodSession(sessionId) {
  if (!sessionId) return;
  window.open(`/pod-session-v2?id=${sessionId}`, "_blank");
}

export function QueueClaimButton({
  session,
  claimed = false,
  claiming = false,
  notTawkOrganizationIds,
  onClaim,
  onView,
}) {
  const claimable = session?.status === "QUEUE" && !claimed;
  const tawkLabel = sessionTawkLabel(session, notTawkOrganizationIds);
  return (
    <Box direction="row" align="center" gap="xsmall" height="32px">
      <Button
        label={claimed ? "View" : "Claim"}
        size="small"
        primary
        disabled={!claimed && (!claimable || claiming)}
        onClick={() => {
          if (claimed) {
            if (onView) onView(session);
            else openPodSession(session?.id);
            return;
          }
          if (!claimable || claiming) return;
          onClaim?.(session);
        }}
      />
      <Text
        size="small"
        color="dark-3"
        style={{ visibility: claimed ? "hidden" : "visible" }}
      >
        {tawkLabel}
      </Text>
      <Text size="small" style={{ visibility: claimed ? "visible" : "hidden" }}>
        Claimed
      </Text>
    </Box>
  );
}

export function WaitingDuration({
  createdAt,
  active = false,
  viewVisible = true,
}) {
  const running = active && viewVisible;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!running) return undefined;
    return subscribeWaitingClock(setNow);
  }, [running]);

  const label = active
    ? formatWaitingDuration(createdAt, running ? now : Date.now())
    : "—";
  const central = formatCreatedAtCentral(createdAt);

  return (
    <Text
      size="small"
      title={central === "—" ? undefined : central}
      aria-label={label}
      style={{
        fontVariantNumeric: "tabular-nums",
        minWidth: "7ch",
        display: "inline-block",
      }}
    >
      {label}
    </Text>
  );
}

export function waitingColumn({ claimedIds, viewVisible = true }) {
  return {
    property: "waiting",
    header: <Text>Waiting</Text>,
    render: (datum) => (
      <WaitingDuration
        createdAt={datum.createdAt}
        active={
          datum.status === "QUEUE" && !claimedIds?.has(datum.id)
        }
        viewVisible={viewVisible}
      />
    ),
    align: "start",
    alignVertical: "middle",
    size: "xsmall",
  };
}

// Data cells keep a fixed xsmall vertical pad (6px). Adding 2.5px on each
// side separates full rows by about 5px. Horizontal pad stays "small".
const queueBodyPad = {
  horizontal: "small",
  vertical: "8.5px",
};

export const queueCompactTablePad = {
  body: {
    horizontal: "6px",
    vertical: "8.5px",
  },
  header: {
    horizontal: "6px",
    vertical: "xsmall",
  },
};

export const queueNotTawkRowClassName = "queue-row-not-tawk";

export const QUEUE_ROW_HIGHLIGHT_CSS = `
tr:has(.${queueNotTawkRowClassName}) td,
tr:has(.${queueNotTawkRowClassName}) td span {
  color: purple !important;
  font-weight: 700 !important;
}
tr:has(.${queueNotTawkRowClassName}) td button,
tr:has(.${queueNotTawkRowClassName}) td button span {
  color: #FFFFFF !important;
  font-weight: 600 !important;
}
tr:has(.${queuePodOutlineRowClassName}) td {
  box-shadow: inset 0 2px 0 orange, inset 0 -2px 0 orange;
}
tr:has(.${queuePodOutlineRowClassName}) td:first-child {
  box-shadow: inset 2px 0 0 orange, inset 0 2px 0 orange, inset 0 -2px 0 orange;
}
tr:has(.${queuePodOutlineRowClassName}) td:last-child {
  box-shadow: inset -2px 0 0 orange, inset 0 2px 0 orange, inset 0 -2px 0 orange;
}
tr:has(.${queuePodOutlineRowClassName}) td:first-child:last-child {
  box-shadow: inset 0 0 0 2px orange;
}
`;

export function QueueRowHighlightStyle() {
  return <style>{QUEUE_ROW_HIGHLIGHT_CSS}</style>;
}

export function queueHighlightClassName(session, notTawkOrganizationIds) {
  const names = [];
  if (isNotTawkSession(session, notTawkOrganizationIds)) {
    names.push(queueNotTawkRowClassName);
  }
  const rowClassName = queueRowClassName(session);
  if (rowClassName) names.push(rowClassName);
  return names.join(" ") || undefined;
}

export function withQueueRowHighlight(columns = [], notTawkOrganizationIds) {
  if (!columns.length) return columns;
  const [first, ...rest] = columns;
  const render = first.render;
  return [
    {
      ...first,
      render: (datum) => {
        const className = queueHighlightClassName(datum, notTawkOrganizationIds);
        return (
          <>
            {className ? (
              <span className={className} data-testid="queue-row-marker" hidden />
            ) : null}
            {render ? render(datum) : datum?.[first.property]}
          </>
        );
      },
    },
    ...rest,
  ];
}

export function QueueDataTable({
  sessions,
  claimedIds,
  columns,
  useFilteredData = false,
  rowProps: rowPropsProp,
  notTawkOrganizationIds,
  pad = { body: queueBodyPad },
}) {
  const { data: filteredData } = useContext(DataContext);
  const tableData = useFilteredData ? filteredData : sessions;
  const rowPropSessions = sessions ?? tableData ?? [];
  const claimedKey = claimedIdsSignature(claimedIds);
  const rowProps = useMemo(() => {
    if (rowPropsProp) return rowPropsProp;
    return queueRowProps(rowPropSessions, claimedIds);
  }, [rowPropsProp, rowPropSessions, claimedIds, claimedKey]);
  const highlightedColumns = useMemo(
    () => withQueueRowHighlight(columns, notTawkOrganizationIds),
    [columns, notTawkOrganizationIds],
  );

  return (
    <>
      <QueueRowHighlightStyle />
      <DataTable
        {...(useFilteredData ? {} : { data: sessions })}
        columns={highlightedColumns}
        primaryKey="id"
        pad={pad}
        rowProps={rowProps}
      />
    </>
  );
}
